import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import StatCard from "./StatCard";
import LoanChart from "./LoanChart";
import CollectionChart from "./CollectionChart";
import RecentTransactions from "./RecentTransactions";
import { db } from "../../firebase";
import { setLoading } from "../../redux/loaderSlice";
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 16px 40px;
  width: 100%;
  box-sizing: border-box;
  animation: fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 769px) {
    margin-top: 24px;
    padding: 0 32px 40px;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: -8px;

  h1 {
    font-size: 28px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textPrimary};
    margin: 0;
    letter-spacing: -0.5px;
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 15px;
    margin: 0;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0,0,0,0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  }
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 24px;
`;

const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN")}`;

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const dateValue = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return "-";
  return dateValue.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getBillingCreatedAt = (bill) => {
  const raw = bill?.createdAt;
  if (!raw) return null;
  if (typeof raw?.toDate === "function") return raw.toDate();
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const [customers, setCustomers] = useState([]);
  const [bills, setBills] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      dispatch(setLoading(true));
      try {
        const customerSnap = await getDocs(collection(db, "customers"));
        const billingQuery = query(collection(db, "billing"), orderBy("createdAt", "desc"));
        const billingSnap = await getDocs(billingQuery);

        setCustomers(customerSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
        setBills(billingSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
      } catch (error) {
        toast.error("Failed to load dashboard data");
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchDashboardData();
  }, [dispatch]);

  console.log(bills);

  const stats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const getStatus = (bill) => {
      const form = bill.form || {};
      const principal = Number(form.loanAmount) || 0;
      const interest = Number(form.interest) || 0;
      const totalWithInterest = principal + (principal * interest / 100);
      const outstanding = bill.outstanding !== undefined ? Number(bill.outstanding) : totalWithInterest;
      if (outstanding <= 0) return "closed";

      const dueDate = form.endDate ? new Date(form.endDate) : null;
      if (dueDate && !Number.isNaN(dueDate.getTime()) && now > dueDate) return "overdue";
      return "active";
    };

    const activeLoans = bills.filter((bill) => getStatus(bill) === "active").length;
    const closedLoans = bills.filter((bill) => getStatus(bill) === "closed").length;
    const overdueLoans = bills.filter((bill) => getStatus(bill) === "overdue").length;

    const todayCollection = bills.reduce((sum, bill) => {
      const payments = bill.payments || [];
      const todayTotal = payments.reduce((acc, p) => {
        if (!p.date) return acc;
        const pDate = new Date(p.date);
        return (
          pDate.getFullYear() === startOfToday.getFullYear() &&
          pDate.getMonth() === startOfToday.getMonth() &&
          pDate.getDate() === startOfToday.getDate()
        ) ? acc + (Number(p.amount) || 0) : acc;
      }, 0);
      return sum + todayTotal;
    }, 0);

    const totalOutstanding = bills.reduce((sum, bill) => {
      const principal = Number(bill.form?.loanAmount) || 0;
      const interest = Number(bill.form?.interest) || 0;
      const totalWithInterest = principal + (principal * interest / 100);
      const outstanding = bill.outstanding !== undefined ? Number(bill.outstanding) : totalWithInterest;
      return sum + outstanding;
    }, 0);

    const goldInventoryValue = bills.reduce((sum, bill) => {
      const isClosed = (bill.payments || []).some(p => p.isClosing === true);
      if (isClosed) return sum;

      const total = (bill.ornaments || []).reduce((innerSum, ornament) => {
        if (ornament.netAmount !== undefined) return innerSum + (Number(ornament.netAmount) || 0);
        const units = Number(ornament.units) || 1;
        const net = Number(ornament.net) || 0;
        const rate = Number(bill.form?.goldRate) || 0;
        return innerSum + (net * units * rate);
      }, 0);
      return sum + total;
    }, 0);

    return {
      totalCustomers: customers.length,
      activeLoans,
      closedLoans,
      overdueLoans,
      todayCollection,
      totalOutstanding,
      goldInventoryValue,
    };
  }, [bills, customers.length]);

  const loanStatusData = useMemo(
    () => [
      { name: "Active", value: stats.activeLoans },
      { name: "Closed", value: stats.closedLoans },
      { name: "Overdue", value: stats.overdueLoans },
    ],
    [stats.activeLoans, stats.closedLoans, stats.overdueLoans]
  );

  const weeklyCollectionData = useMemo(() => {
    const today = new Date();
    const days = Array.from({ length: 5 }).map((_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() - (4 - index));
      return {
        key: `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`,
        day: date.toLocaleDateString("en-US", { weekday: "short" }),
        amount: 0,
      };
    });

    const mapped = new Map(days.map((item) => [item.key, item]));
    bills.forEach((bill) => {
      const payments = bill.payments || [];
      payments.forEach((p) => {
        if (!p.date) return;
        const pDate = new Date(p.date);
        const key = `${pDate.getFullYear()}-${pDate.getMonth()}-${pDate.getDate()}`;
        if (!mapped.has(key)) return;
        mapped.get(key).amount += Number(p.amount) || 0;
      });
    });

    return days.map(({ day, amount }) => ({ day, amount }));
  }, [bills]);

  const recentTransactions = useMemo(() => {
    const allTransactions = [];

    bills.forEach((bill) => {
      const principal = Number(bill.form?.loanAmount) || 0;
      allTransactions.push({
        id: bill.customerId || bill.id,
        customer: bill.form?.customerName || "-",
        amount: formatCurrency(principal),
        type: "Loan Issued",
        rawDate: getBillingCreatedAt(bill) || new Date(0),
        status: "loan",
      });

      if (bill.payments && Array.isArray(bill.payments)) {
        bill.payments.forEach((p) => {
          allTransactions.push({
            id: bill.customerId || bill.id,
            customer: bill.form?.customerName || "-",
            amount: formatCurrency(p.amount),
            type: "Repayment",
            rawDate: new Date(p.date),
            status: "paid",
          });
        });
      }
    });

    allTransactions.sort((a, b) => b.rawDate - a.rawDate);

    return allTransactions.slice(0, 8).map(t => ({
      ...t,
      date: formatDisplayDate(t.rawDate)
    }));
  }, [bills]);

  return (
    <Wrapper>
      <HeaderSection style={{ marginTop: "40px" }}>
        <h1>Dashboard Overview</h1>
        <p>Monitor your collections, active loans, and performance.</p>
      </HeaderSection>

      <Grid>
        <StatCard title="Total Customers" value={stats.totalCustomers.toLocaleString("en-IN")} />
        <StatCard title="Active Loans" value={stats.activeLoans.toLocaleString("en-IN")} />
        <StatCard title="Today's Collection" value={formatCurrency(stats.todayCollection)} />
        <StatCard title="Total Outstanding" value={formatCurrency(stats.totalOutstanding)} />
        {/* <StatCard title="Gold Inventory Value" value={formatCurrency(stats.goldInventoryValue)} /> */}
        <StatCard title="Overdue Loans" value={stats.overdueLoans.toLocaleString("en-IN")} />
      </Grid>

      <ChartGrid>

        <ChartCard>
          <ChartTitle>Loan Status Overview</ChartTitle>
          <LoanChart data={loanStatusData} />
        </ChartCard>

        <ChartCard>
          <ChartTitle>Weekly Collection</ChartTitle>
          <CollectionChart data={weeklyCollectionData} />
        </ChartCard>

      </ChartGrid>
      <RecentTransactions data={recentTransactions} />

    </Wrapper>
  );
};

export default Dashboard;